<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import SalesPendingChargeAuthorizationBar from '../components/SalesPendingChargeAuthorizationBar.vue';
import SalesPendingChargeConfirmation from '../components/SalesPendingChargeConfirmation.vue';
import SalesPendingChargePayers from '../components/SalesPendingChargePayers.vue';
import SalesPendingChargePayerEditor from '../components/SalesPendingChargePayerEditor.vue';
import SalesPendingChargeSummary from '../components/SalesPendingChargeSummary.vue';
import { useSalesPendingCharge } from '../composables/useSalesPendingCharge';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const transactionId = computed(() =>
  route.params.salesTransactionId?.toString()
);
const installmentId = computed(() => route.params.installmentId?.toString());
const state = useSalesPendingCharge({
  transactionId,
  installmentId,
  t,
  notify: useAlert,
});
const agreed = ref(false);
const editing = ref(false);
const confirmationVisible = ref(false);

const responsibilities = computed(
  () => state.charge.value?.dados_parcela?.responsaveis_pagamento || []
);
const canAuthorize = computed(
  () =>
    agreed.value &&
    responsibilities.value.length > 0 &&
    state.actions.value.pode_autorizar_cobranca
);
const address = computed(() => {
  const data = state.charge.value?.dados_processo || {};
  const street = [data.logradouro, data.numero].filter(Boolean).join(', ');
  const details = [data.unidade, data.complemento].filter(Boolean).join(', ');
  const city = [data.cidade, data.uf].filter(Boolean).join(' - ');
  return [street, details, data.bairro, city].filter(Boolean).join(', ');
});

const backToSales = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas',
    params: { accountId: route.params.accountId },
  });

const handleBack = () => {
  if (editing.value) {
    editing.value = false;
    return;
  }
  backToSales();
};

const savePayers = async rows => {
  const saved = await state.updatePayers(rows);
  if (saved) {
    editing.value = false;
    agreed.value = false;
  }
};

const authorizeCharge = async () => {
  if (!canAuthorize.value) return;
  const authorized = await state.authorize();
  if (authorized) confirmationVisible.value = true;
};

watch([state.completed, state.authorizedNow], ([completed, authorizedNow]) => {
  if (completed && !authorizedNow) backToSales();
});
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft pb-24">
    <LaisHeader
      :title="t('SALES.PENDING_CHARGE.TITLE')"
      :subtitle="address || t('SALES.COMMON.ADDRESS_UNAVAILABLE')"
      :breadcrumb="[
        { label: t('SALES.TITLE') },
        { label: t('SALES.PENDING_CHARGE.TITLE') },
      ]"
      show-back
      :back-label="t('SALES.ACTIONS.BACK')"
      @back="handleBack"
    />

    <main
      class="mx-auto grid w-full max-w-[1180px] gap-5 px-4 sm:px-6 lg:px-10"
    >
      <div
        v-if="state.loading.value"
        class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
      >
        {{ t('SALES.COMMON.LOADING') }}
      </div>
      <div
        v-else-if="state.error.value"
        class="rounded-lais border border-lais-danger/30 bg-white p-8 text-center text-sm text-lais-danger"
      >
        {{ state.error.value }}
      </div>
      <SalesPendingChargePayerEditor
        v-if="editing && state.charge.value"
        :charge="state.charge.value"
        :loading="state.saving.value"
        @save="savePayers"
      />
      <template v-else-if="state.charge.value">
        <section
          class="rounded-lais border border-lais-border-soft bg-white p-5"
        >
          <h1 class="font-redHatDisplay text-xl font-bold text-lais-text">
            {{ t('SALES.PENDING_CHARGE.INTRO.TITLE') }}
          </h1>
          <p class="mt-2 text-sm text-lais-text-body">
            {{ t('SALES.PENDING_CHARGE.INTRO.SUBTITLE') }}
          </p>
        </section>
        <SalesPendingChargeSummary
          :charge="state.charge.value"
          :downloading="state.downloading.value"
          @download="state.downloadSheet"
        />
        <SalesPendingChargePayers
          :charge="state.charge.value"
          @edit="editing = true"
        />
      </template>
    </main>

    <SalesPendingChargeAuthorizationBar
      v-if="state.charge.value && !state.completed.value && !editing"
      v-model="agreed"
      :agreement-disabled="
        state.authorizing.value || responsibilities.length === 0
      "
      :authorizing="state.authorizing.value"
      :can-authorize="canAuthorize"
      @authorize="authorizeCharge"
    />

    <SalesPendingChargeConfirmation
      :show="confirmationVisible"
      :address="address"
      @close="backToSales"
      @confirm="backToSales"
    />
  </div>
</template>
